/**
 * What the user actually filled in, per use case.
 *
 * Keyed by the `task` string the form POSTs — NOT the use-case id from UseCaseSelector. The two
 * diverge in several places (`attribution` sends `ab-test-messaging`, `content-testing` sends
 * `a/b-multivariate-tests`, `create-content` splits into `create-content`/`create-content-long`),
 * and `task` is what a stored simulation actually carries.
 *
 * Field order here is the display order — it mirrors the order the fields appear in the form, so
 * a rater reads the brief the way its author wrote it.
 *
 * `from` names the envelope the value lives in. Payload nesting is inconsistent across the 21 live
 * forms: most are flat, three nest under `data`, BuyerInsights uses `additional_data`.
 *
 * This registry is a display concern only. It never gates what is sent or stored, and a task that
 * is absent here degrades gracefully — every non-plumbing key falls through to "Other inputs".
 */

export type InputEnvelope = 'root' | 'data' | 'additional_data' | 'user_product';

/**
 * The container keys a nested field can live under. Single source of truth: the selector reads
 * envelopes through this, and the hidden-key set suppresses these containers so an envelope is
 * never ALSO dumped whole as raw JSON next to the fields extracted from it. Adding a fifth
 * envelope here wires up both behaviours at once.
 */
export const ENVELOPE_KEYS: Readonly<Record<Exclude<InputEnvelope, 'root'>, string>> = {
  data: 'data',
  additional_data: 'additional_data',
  user_product: 'user_product',
};

export interface InputFieldDef {
  key: string;
  label: string;
  from?: InputEnvelope;
}

export interface UseCaseInputDef {
  /** Human name for the use case, shown as a badge on the rating inputs panel. */
  label: string;
  fields: InputFieldDef[];
}

const NAME_FIELD: InputFieldDef = { key: 'name', label: 'Simulation name' };

export const USE_CASE_INPUT_REGISTRY: Readonly<Record<string, UseCaseInputDef>> = {
  'concept-test': {
    label: 'Concept test',
    fields: [
      NAME_FIELD,
      { key: 'headline', label: 'Headline' },
      { key: 'consumer_insight', label: 'Consumer insight' },
      { key: 'main_benefit', label: 'Main benefit' },
      { key: 'reasons_to_believe', label: 'Reasons to believe' },
      { key: 'price_point', label: 'Price point' },
      { key: 'pack_size', label: 'Pack size' },
      { key: 'brand', label: 'Brand' },
      { key: 'alternative_products', label: 'Alternative products' },
    ],
  },

  'vitamin-concept-test': {
    label: 'Vitamin concept test',
    fields: [
      NAME_FIELD,
      { key: 'concept_str', label: 'Concept' },
      { key: 'research_question', label: 'Research question' },
    ],
  },

  'driving-concept-test': {
    label: 'Driving concept test',
    fields: [
      NAME_FIELD,
      { key: 'concept_statement', label: 'Concept statement' },
      { key: 'weather', label: 'Weather' },
      { key: 'traffic', label: 'Traffic' },
    ],
  },

  claims_testing: {
    label: 'Claims testing',
    fields: [
      NAME_FIELD,
      { key: 'product_name', label: 'Product name', from: 'data' },
      { key: 'product_description', label: 'Product description', from: 'data' },
      { key: 'category', label: 'Category', from: 'data' },
      // `claims` is deliberately NOT mapped: SimulationInputsDropdown renders a dedicated
      // "Claims (N)" block for this task, which stays visible in the rating view. Mapping it
      // here too would list the same claims twice.
      { key: 'context', label: 'Context', from: 'data' },
    ],
  },

  'get-insights': {
    label: 'Insights',
    fields: [NAME_FIELD, { key: 'context', label: 'Context' }],
  },

  survey_and_focus_groups: {
    label: 'Survey & focus groups',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'context', label: 'Context' },
      { key: 'use_internal_debate', label: 'Internal debate' },
      { key: 'enable_adaptive_persona_context', label: 'Adaptive persona context' },
    ],
  },

  'packaging-review-v2': {
    label: 'Packaging review',
    fields: [
      NAME_FIELD,
      { key: 'goal_of_simulation', label: 'Goal of simulation' },
      { key: 'brand', label: 'Brand', from: 'data' },
      { key: 'goal', label: 'Goal', from: 'data' },
    ],
  },

  'packaging-variant-comparison': {
    label: 'Packaging variant comparison',
    fields: [
      NAME_FIELD,
      { key: 'goal_of_simulation', label: 'Goal of simulation' },
      { key: 'brand', label: 'Brand', from: 'data' },
      { key: 'goal', label: 'Goal', from: 'data' },
    ],
  },

  'packaging-review-gemini': {
    label: 'Packaging review',
    fields: [NAME_FIELD, { key: 'goal', label: 'Goal' }],
  },

  'creative-review': {
    label: 'Creative review',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'campaign_objective', label: 'Campaign objective' },
      { key: 'kpis', label: 'KPIs' },
      { key: 'company_details', label: 'Company details' },
      { key: 'product_details', label: 'Product details' },
      { key: 'competitive_landscape', label: 'Competitive landscape' },
      { key: 'medium_and_placement', label: 'Medium & placement' },
      { key: 'cultural_considerations', label: 'Cultural considerations' },
      { key: 'prior_performance_data', label: 'Prior performance data' },
    ],
  },

  'channel-event-strategy': {
    label: 'Channel & event strategy',
    fields: [NAME_FIELD, { key: 'goal', label: 'Goal' }],
  },

  'ab-test-creatives-gemini': {
    label: 'A/B test creatives',
    fields: [NAME_FIELD, { key: 'goal', label: 'Goal' }],
  },

  'ab-test-messaging': {
    label: 'A/B test messaging',
    fields: [
      NAME_FIELD,
      { key: 'objective', label: 'Objective' },
      { key: 'placement', label: 'Placement' },
      { key: 'context', label: 'Context' },
      { key: 'marketing_copies', label: 'Marketing copies' },
    ],
  },

  'a/b-multivariate-tests': {
    label: 'A/B & multivariate test',
    fields: [
      NAME_FIELD,
      { key: 'objective', label: 'Objective' },
      { key: 'test_type', label: 'Test type' },
      { key: 'input_messages', label: 'Messages under test' },
    ],
  },

  'image-survey': {
    label: 'Image survey',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'creative_type', label: 'Creative type' },
      { key: 'industry', label: 'Industry' },
      { key: 'placement_goal', label: 'Placement goal' },
    ],
  },

  logo_test: {
    label: 'Logo test',
    fields: [NAME_FIELD, { key: 'goal', label: 'Goal' }],
  },

  'create-content': {
    label: 'Content creation',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'content_type', label: 'Content type' },
      { key: 'content_subject', label: 'Content subject' },
      { key: 'company_context', label: 'Company context' },
    ],
  },

  'create-content-long': {
    label: 'Long-form content creation',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'context', label: 'Context' },
      { key: 'content_talk_about', label: 'Content focus' },
      { key: 'content_type', label: 'Content type' },
      { key: 'content_subject', label: 'Content subject' },
      { key: 'company_context', label: 'Company context' },
    ],
  },

  'pricing-analysis': {
    label: 'Pricing analysis',
    fields: [
      NAME_FIELD,
      { key: 'objective', label: 'Objective' },
      { key: 'price_tiers', label: 'Price tiers' },
      { key: 'product_description', label: 'Product description' },
      { key: 'value_proposition', label: 'Value proposition' },
    ],
  },

  'buyer-insights-report-b2b': {
    label: 'Buyer insights (B2B)',
    fields: [
      NAME_FIELD,
      { key: 'context', label: 'Context' },
      { key: 'product_name', label: 'Product name', from: 'additional_data' },
      { key: 'website_url', label: 'Website', from: 'additional_data' },
    ],
  },

  'buyer-insights-report-b2c': {
    label: 'Buyer insights (B2C)',
    fields: [
      NAME_FIELD,
      { key: 'context', label: 'Context' },
      { key: 'product_name', label: 'Product name', from: 'additional_data' },
      { key: 'website_url', label: 'Website', from: 'additional_data' },
    ],
  },

  create_campaign_strategy: {
    label: 'Campaign strategy',
    fields: [
      NAME_FIELD,
      { key: 'goal', label: 'Goal' },
      { key: 'product', label: 'Product' },
      { key: 'product_website', label: 'Product website' },
      { key: 'budget', label: 'Budget' },
      { key: 'team', label: 'Team' },
      { key: 'brand_position', label: 'Brand position' },
      { key: 'timeline', label: 'Timeline' },
      { key: 'theme', label: 'Theme' },
      { key: 'brandvoice', label: 'Brand voice' },
    ],
  },

  test_use_case: {
    label: 'Test use case',
    fields: [NAME_FIELD, { key: 'goal', label: 'Goal' }, { key: 'context', label: 'Context' }],
  },
};

export const getUseCaseInputDef = (task: unknown): UseCaseInputDef | null =>
  typeof task === 'string' ? (USE_CASE_INPUT_REGISTRY[task] ?? null) : null;
